<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSidebar } from '../../composables/useSidebar'
import { useUserProfileStore } from '../../stores/userProfile'
import AppIcon from '../ui/AppIcon.vue'

const { t } = useI18n()
const route = useRoute()
const { isOpen, close } = useSidebar()
const profileStore = useUserProfileStore()

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + '/')
}

interface NavItem {
  to: string
  label: string
  icon: string
  disabled?: boolean
}

const sections = computed<{ title: string; items: NavItem[] }[]>(() => [
  {
    title: t('sidebar.operations'),
    items: [
      { to: '/dashboard', label: t('sidebar.allPlans'), icon: 'description' },
      { to: '/flight-monitor', label: t('sidebar.flightStatus'), icon: 'flight' },
      ...(profileStore.usesExternalPartners
        ? [{ to: '/coach-partners', label: t('sidebar.servicePartners'), icon: 'directions_bus' }]
        : []),
    ],
  },
  {
    title: t('sidebar.configuration'),
    items: [
      { to: '/locations', label: t('sidebar.locationsAirports'), icon: 'location_on' },
    ],
  },
  {
    title: t('sidebar.settings'),
    items: [
      { to: '/account', label: t('sidebar.account'), icon: 'person' },
      { to: '/support', label: t('sidebar.support'), icon: 'help' },
    ],
  },
])
</script>

<template>
  <!-- Mobile backdrop -->
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/30 z-40 lg:hidden"
      @click="close"
    />
  </Teleport>

  <aside
    :class="[
      'fixed top-14 bottom-0 left-0 w-52 bg-surface-container-low z-40 flex flex-col overflow-y-auto transition-transform duration-200',
      'lg:translate-x-0',
      isOpen ? 'translate-x-0' : '-translate-x-full'
    ]"
  >
    <nav class="flex-1 py-4 px-3 space-y-6">
      <div v-for="section in sections" :key="section.title">
        <p class="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-on-surface-variant font-mono">
          {{ section.title }}
        </p>
        <template v-for="item in section.items" :key="item.to">
          <button
            v-if="item.disabled"
            disabled
            class="flex items-center gap-2.5 px-2 py-1.5 rounded text-sm font-medium text-outline w-full cursor-not-allowed"
          >
            <AppIcon :name="item.icon" size="sm" />
            {{ item.label }}
          </button>
          <router-link
            v-else
            :to="item.to"
            :aria-current="isActive(item.to) ? 'page' : undefined"
            :class="[
              'flex items-center gap-2.5 px-2 py-1.5 rounded text-sm font-medium transition-colors',
              isActive(item.to)
                ? 'bg-surface-container-high text-primary'
                : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
            ]"
            @click="close"
          >
            <AppIcon :name="item.icon" size="sm" />
            {{ item.label }}
          </router-link>
        </template>
      </div>
    </nav>
  </aside>
</template>
